/**
 * Quote Statistics Service
 *
 * Handles all statistical and analytical operations for quotes.
 * This service provides aggregated data, metrics, and reports.
 */

import { RepositoryResult } from './BaseRepository';
import { getDbClient } from '@/server/lib/database';

/**
 * Quote statistics
 */
export type QuoteStats = {
  total: number;
  draft: number;
  sent: number;
  accepted: number;
  rejected: number;
  totalValue: number;
  acceptedValue: number;
  pendingValue: number;
};

/**
 * Quote performance metrics
 */
export type QuotePerformanceMetrics = {
  averageQuoteValue: number;
  acceptanceRate: number;
  conversionRate: number; // Quotes converted to invoices
  mostCommonStatus: string;
  valueByStatus: Record<string, number>;
};

/**
 * Quote Statistics Service
 * Provides statistical and analytical operations for quotes
 */
export class QuoteStatisticsService {
  private tableName = 'quotes' as const;

  /**
   * Get quote statistics for a user
   */
  async getStats(userId: string): Promise<RepositoryResult<QuoteStats>> {
    try {
      const db = getDbClient();

      const { data, error } = await db
        .from(this.tableName)
        .select('status, total_amount')
        .eq('user_id', userId);

      if (error) {
        throw error;
      }

      const quotes = data || [];

      const stats: QuoteStats = {
        total: quotes.length,
        draft: 0,
        sent: 0,
        accepted: 0,
        rejected: 0,
        totalValue: 0,
        acceptedValue: 0,
        pendingValue: 0,
      };

      quotes.forEach((quote) => {
        // Update status count using type-safe approach
        if (quote.status in stats) {
          stats[quote.status as keyof typeof stats]++;
        }
        stats.totalValue += quote.total_amount || 0;

        if (quote.status === 'accepted') {
          stats.acceptedValue += quote.total_amount || 0;
        }

        if (quote.status === 'sent' || quote.status === 'draft') {
          stats.pendingValue += quote.total_amount || 0;
        }
      });

      return {
        success: true,
        data: stats,
      };
    } catch (error) {
      return {
        success: false,
        error: this.handleError(error, 'fetching quote statistics'),
      };
    }
  }

  /**
   * Get quote performance metrics
   */
  async getPerformanceMetrics(userId: string): Promise<RepositoryResult<QuotePerformanceMetrics>> {
    try {
      const db = getDbClient();

      const { data, error } = await db
        .from(this.tableName)
        .select('status, total_amount')
        .eq('user_id', userId);

      if (error) {
        throw error;
      }

      const quotes = data || [];

      if (quotes.length === 0) {
        return {
          success: true,
          data: {
            averageQuoteValue: 0,
            acceptanceRate: 0,
            conversionRate: 0,
            mostCommonStatus: 'draft',
            valueByStatus: {},
          },
        };
      }

      // Calculate average quote value
      const totalValue = quotes.reduce((sum, quote) => sum + (quote.total_amount || 0), 0);
      const averageQuoteValue = totalValue / quotes.length;

      // Calculate acceptance rate
      const acceptedQuotes = quotes.filter((quote) => quote.status === 'accepted').length;
      const sentQuotes = quotes.filter((quote) => quote.status === 'sent' || quote.status === 'accepted').length;
      const acceptanceRate = sentQuotes > 0 ? (acceptedQuotes / sentQuotes) * 100 : 0;

      // Calculate conversion rate (quotes converted to invoices)
      const { count: invoiceCount } = await db
        .from('invoices')
        .select('id', { count: 'exact', head: true })
        .eq('user_id', userId)
        .not('quote_id', 'is', null);

      const conversionRate = quotes.length > 0 ? ((invoiceCount || 0) / quotes.length) * 100 : 0;

      // Find most common status
      const statusCount: Record<string, number> = {};
      quotes.forEach((quote) => {
        statusCount[quote.status] = (statusCount[quote.status] || 0) + 1;
      });

      const mostCommonStatus = Object.entries(statusCount).sort((a, b) => b[1] - a[1])[0][0];

      // Value by status
      const valueByStatus: Record<string, number> = {};
      quotes.forEach((quote) => {
        valueByStatus[quote.status] = (valueByStatus[quote.status] || 0) + (quote.total_amount || 0);
      });

      return {
        success: true,
        data: {
          averageQuoteValue,
          acceptanceRate,
          conversionRate,
          mostCommonStatus,
          valueByStatus,
        },
      };
    } catch (error) {
      return {
        success: false,
        error: this.handleError(error, 'fetching quote performance metrics'),
      };
    }
  }

  /**
   * Get revenue by month for quotes
   */
  async getValueByMonth(
    userId: string,
    months: number = 12
  ): Promise<RepositoryResult<Array<{ month: string; year: number; value: number }>>> {
    try {
      const db = getDbClient();

      const startDate = new Date();
      startDate.setMonth(startDate.getMonth() - months);

      const { data, error } = await db
        .from(this.tableName)
        .select('total_amount, created_at')
        .eq('user_id', userId)
        .gte('created_at', startDate.toISOString());

      if (error) {
        throw error;
      }

      const quotes = data || [];

      // Group by month
      const monthlyValue = new Map<string, number>();

      quotes.forEach((quote) => {
        const date = new Date(quote.created_at);
        const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;

        monthlyValue.set(monthKey, (monthlyValue.get(monthKey) || 0) + (quote.total_amount || 0));
      });

      // Convert to array
      const valueByMonth = Array.from(monthlyValue.entries()).map(([key, value]) => {
        const [year, month] = key.split('-');
        const date = new Date(parseInt(year), parseInt(month) - 1);

        return {
          month: date.toLocaleString('default', { month: 'short' }),
          year: parseInt(year),
          value,
        };
      });

      return {
        success: true,
        data: valueByMonth,
      };
    } catch (error) {
      return {
        success: false,
        error: this.handleError(error, 'fetching quote value by month'),
      };
    }
  }

  /**
   * Get expiring quotes (quotes valid until soon)
   */
  async getExpiringQuotes(
    userId: string,
    days: number = 7
  ): Promise<RepositoryResult<{ count: number; totalValue: number }>> {
    try {
      const db = getDbClient();

      const now = new Date();
      const futureDate = new Date();
      futureDate.setDate(futureDate.getDate() + days);

      const { data, error } = await db
        .from(this.tableName)
        .select('total_amount')
        .eq('user_id', userId)
        .in('status', ['draft', 'sent'])
        .gte('valid_until', now.toISOString())
        .lte('valid_until', futureDate.toISOString());

      if (error) {
        throw error;
      }

      const quotes = data || [];
      const count = quotes.length;
      const totalValue = quotes.reduce((sum, quote) => sum + (quote.total_amount || 0), 0);

      return {
        success: true,
        data: {
          count,
          totalValue,
        },
      };
    } catch (error) {
      return {
        success: false,
        error: this.handleError(error, 'fetching expiring quotes'),
      };
    }
  }

  /**
   * Get expired quotes
   */
  async getExpiredQuotes(userId: string): Promise<RepositoryResult<{ count: number; totalValue: number }>> {
    try {
      const db = getDbClient();

      const now = new Date().toISOString();

      const { data, error } = await db
        .from(this.tableName)
        .select('total_amount')
        .eq('user_id', userId)
        .in('status', ['draft', 'sent'])
        .lt('valid_until', now);

      if (error) {
        throw error;
      }

      const quotes = data || [];
      const count = quotes.length;
      const totalValue = quotes.reduce((sum, quote) => sum + (quote.total_amount || 0), 0);

      return {
        success: true,
        data: {
          count,
          totalValue,
        },
      };
    } catch (error) {
      return {
        success: false,
        error: this.handleError(error, 'fetching expired quotes'),
      };
    }
  }

  /**
   * Helper method to handle errors consistently
   */
  private handleError(error: any, context: string): string {
    console.error(`Error ${context}:`, error);
    return error?.message || `Failed to ${context}`;
  }
}

// Export singleton instance
export const quoteStatisticsService = new QuoteStatisticsService();
